import type {EnvelopeInterface} from '@/store/modules/envelope.ts';
import type {Employee, RenterInterface, User} from '@/types/user';
import type {OrderType} from '@/types/order';
import type {ListingInterface, ListingPricingPeriod, ListingPricingPriceType} from '@/types/listing';
import type {Timestamps, Uuid} from '@/types/common.ts';
import type {StripeVerification} from '@/types/Stripe.ts';

export enum BookingStatus {
    PENDING = 'pending',
    PENDING_APPROVAL = 'pending_approval',
    CANCELLED = 'cancelled',
    CANCELED = 'canceled',
    COMPLETED = 'completed',
    ARCHIVED = 'archived',
    CONCEPT = 'concept',
    RESERVED = 'reserved',
    STARTED = 'started',
    IN_PROGRESS = 'in_progress',
    STOPPED = 'stopped',
    UNAVAILABLE = 'unavailable',
}

export interface BookingFilter {
    payment_status?: Array<string>,
    status?: Array<string>,
    source?: Array<string>,
    starts_at_gt?: string,
    auto_extend?: number,
}

export interface BookingMeta extends Timestamps, Uuid {
    booking_id: string,
    createdBy: string,
    createdByInitials: string,
    createdByPhotoUrl: string,
    id: number,
    meta_key: string,
    meta_value: string,
}

export interface BookingExtension extends Timestamps, Uuid {
    comments: string,
    employee: Employee,
    pending: boolean,
    trip_end: string,
    trip_start: string,
}

export interface BookingInterface extends Timestamps {
    /** Dashboard only: the renter's key for signing and photo uploads on the storefront. */
    guest_key?: string | null;
    init?: boolean,
    custodian?: any,
    extension_plan_status?: string | null,
    b_id: string | null,
    cartId?: string | null,
    booking_meta: BookingMeta[] | null | undefined,
    complete: number,
    host: User | null,
    days: number,
    customer: object
    settings: object,
    trip_displays: object,
    mile_allowance: object,
    duration: object,
    delivery: object,
    location: object,
    locations: object[],
    notes?: string,
    trip_required_options: any[],
    provider_bookings?: any[],
    partnerCommission?: any,
    pricing: {
        original_price: number,
        price: number,
        price_type: ListingPricingPriceType,
        price_period: ListingPricingPeriod,
        discounts: {
            success: boolean,
            loading: boolean,
            discounted: boolean,
            code: string,
            discount: string,
            discountValue: number,
            newPrice: number,
            type: string,
            reason: string,
        },
        delivery: number,
        extras: number,
        requiredOption: number,
        total_per_day: number,
        subTotal: number,
        grandTotal: number,
        paymentDisplay: string,
        security_deposit: number,
    },
    vehicle: object,
    delivery_price: number,
    discount: number,
    extras_price: number,
    fuel_level_end?: number,
    fuel_level_start?: number,
    l_id: string | null,
    listing?: ListingInterface | null,
    manually_deleted_at: string | null,
    method: string,
    miles_extra: number
    miles_included: number,
    odometer_end?: number | null,
    odometer_start?: number | null,
    /** Pickup / return readings from a connected car (Tesla), oldest first. */
    vehicle_readings?: BookingVehicleReading[],
    properties?: [],
    payment_status: string,
    price_per_day: number,
    r_id: string,
    renter: RenterInterface | null | undefined,
    required_options_price: number,
    source: string,
    status: BookingStatus,
    taxes: number,
    timezone: string,
    total: number,
    trip_end: string,
    trip_end_tz: string,
    trip_start: string,
    trip_start_tz: string,
    turo_trip?: {
        id?: string,
        status?: string,
        address?: string,
        start_trip_display?: string,
        end_trip_display?: string,
        vehicle_id?: string,
    } | null,
    getaround_trip?: any,
    envelope?: EnvelopeInterface,
    extensions?: BookingExtension[],
    order?: OrderType,
    /** Provider sync bookings (Outdoorsy, Turo, HQ Rentals, Getaround) */
    reservation_object?: unknown,
    stage?: string,
    provider_booking_id?: string,
    stripeIdentity?: StripeVerification[]
}

/** Outdoorsy-specific reservation payload (reservation_object shape) */
export interface OutdoorsyReservationObject {
    renter_summary?: { first_name?: string; last_name?: string };
    rental_summary?: { name?: string };
    owner_payout?: number;
}

/** Booking from Outdoorsy provider sync (has reservation_object + stage/provider_booking_id) */
export type OutdoorsyBookingInterface = BookingInterface & {
    uuid?: string;
    reservation_object?: OutdoorsyReservationObject;
    stage?: string;
    provider_booking_id?: string;
};

export interface BookingVehicleReading {
    phase: 'start' | 'end',
    source: 'tesla',
    odometer: number | null,
    distance_unit: 'miles' | 'kilometers',
    battery_level: number | null,
    captured_at: string,
}
